/**
 * XSS Protection Service
 * Provides XSS protection utilities and content sanitization
 */

import { sanitizeHTML, sanitizeQuillContent, escapeHTML, stripHTML } from '../utils/htmlSanitization'
import { detectXSS, sanitizeXSS } from '../utils/inputSanitization'
import { captureException } from './errorTracking'
import { detectBreachEvent } from './breachDetection'

/**
 * Sanitize user input for display
 */
export const sanitizeForDisplay = (input, options = {}) => {
  if (input === null || input === undefined) {
    return ''
  }

  if (typeof input !== 'string') {
    return String(input)
  }

  // Check for XSS patterns
  if (detectXSS(input)) {
    logXSSAttempt(input, options)
    
    // If strict mode, throw error
    if (options.strict) {
      throw new Error('XSS attempt detected')
    }
  }

  // Sanitize based on options
  if (options.allowHTML) {
    // Allow HTML but sanitize it
    return sanitizeHTML(input)
  } else {
    // Escape HTML entities
    return escapeHTML(input)
  }
}

/**
 * Sanitize rich text content (React Quill)
 */
export const sanitizeRichText = (content) => {
  if (!content) {
    return ''
  }

  // Check for XSS patterns
  const contentString = typeof content === 'string' ? content : JSON.stringify(content)
  if (detectXSS(contentString)) {
    logXSSAttempt(contentString, { type: 'rich_text' })
  }

  // Sanitize Quill content
  return sanitizeQuillContent(content)
}

/**
 * Sanitize form data
 */
export const sanitizeFormData = (formData, options = {}) => {
  const sanitized = {}

  for (const key in formData) {
    if (Object.prototype.hasOwnProperty.call(formData, key)) {
      const value = formData[key]

      if (typeof value === 'string') {
        // Check for XSS
        if (detectXSS(value)) {
          logXSSAttempt(value, { field: key, type: 'form_data' })
          
          if (options.strict) {
            throw new Error(`XSS attempt detected in field: ${key}`)
          }
        }

        // Sanitize based on field type
        if (options.richTextFields?.includes(key)) {
          sanitized[key] = sanitizeRichText(value)
        } else if (options.allowHTMLFields?.includes(key)) {
          sanitized[key] = sanitizeHTML(value)
        } else {
          sanitized[key] = sanitizeXSS(value)
        }
      } else if (Array.isArray(value)) {
        sanitized[key] = value.map((item) =>
          typeof item === 'string' ? sanitizeXSS(item) : item
        )
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = sanitizeFormData(value, options)
      } else {
        sanitized[key] = value
      }
    }
  }

  return sanitized
}

/**
 * Log XSS attempt
 */
const logXSSAttempt = async (input, context = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    const { data: { session } } = await supabase.auth.getSession()

    // Get organization ID if available
    let organizationId = null
    if (user) {
      const { data: orgMember } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)
        .single()

      organizationId = orgMember?.organization_id
    }

    // Log to audit logs
    await supabase.from('audit_logs').insert({
      organization_id: organizationId,
      user_id: user?.id,
      action_type: 'security_violation',
      resource_type: 'system',
      details: {
        violation_type: 'xss_attempt',
        ...context,
        input_preview: input.substring(0, 200), // Log first 200 chars
      },
      ip_address: await getClientIP(),
      user_agent: navigator.userAgent,
    })

    // Trigger breach detection
    if (organizationId) {
      await detectBreachEvent(
        organizationId,
        'unauthorized_access',
        'high',
        `XSS attempt detected: ${context.reason || 'Malicious script detected'}`,
        [],
        user ? [user.id] : [],
        await getClientIP()
      )
    }
  } catch (error) {
    // Don't throw - logging should not break the app
    console.error('Failed to log XSS attempt:', error)
  }
}

/**
 * Get client IP (approximation)
 */
const getClientIP = async () => {
  try {
    // Note: This may be blocked by ad blockers - that's okay
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 3000) // 3 second timeout
    
    const response = await fetch('https://api.ipify.org?format=json', {
      signal: controller.signal,
    })
    
    clearTimeout(timeoutId)
    
    if (!response.ok) {
      return null
    }
    
    const data = await response.json()
    return data.ip
  } catch (error) {
    // Silently fail - IP fetching is optional
    return null
  }
}

/**
 * Safe HTML renderer component props
 */
export const getSafeHTMLProps = (html, options = {}) => {
  const sanitized = sanitizeHTML(html)

  return {
    dangerouslySetInnerHTML: {
      __html: sanitized,
    },
    ...options,
  }
}

/**
 * Validate URL for safe linking
 */
export const validateSafeURL = (url) => {
  if (typeof url !== 'string') {
    return false
  }

  try {
    const parsedUrl = new URL(url, window.location.href)
    const allowedProtocols = ['http:', 'https:', 'mailto:']

    if (!allowedProtocols.includes(parsedUrl.protocol)) {
      return false
    }

    // Check for javascript: or data: protocols
    if (parsedUrl.protocol === 'javascript:' || parsedUrl.protocol.startsWith('data:')) {
      return false
    }

    return true
  } catch (e) {
    return false
  }
}

export default {
  sanitizeForDisplay,
  sanitizeRichText,
  sanitizeFormData,
  getSafeHTMLProps,
  validateSafeURL,
}

